import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check } from 'lucide-react';
import { vipPrivateAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Spinner } from '../ui/spinner';
import { Alert, AlertDescription } from '../ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';
import { formatAmount, storedAmount } from '../vip/bonus_requests/format';
import { rateLine } from '../vip/bonus_requests/resultCardView';
import {
  accountFor, applyRounding, undoRounding, bonusFormError, bonusPayload, bonusUSD, initialBonusForm, needsManualApproval,
  withAmount, withCurrency, withMode, withUSD,
} from './bonusForm';
import BonusAmountFields from './BonusAmountFields';

const usdText = (cents) => (cents == null ? 'no rate' : `${formatAmount((cents / 100).toFixed(2))} USD`);

function Field({ label, htmlFor, className, children }) {
  return (
    <div className={cn('min-w-0', className)}>
      <label htmlFor={htmlFor} className="mb-1.5 flex h-[30px] items-center text-xs font-bold uppercase tracking-wider text-ink-dim">{label}</label>
      {children}
    </div>
  );
}

// IssueBonusForm: the private dashboard's issue-bonus form, opened inside the player's identity
// card. The amount is typed in the account's currency or in USD (a toggle); the other value is
// converted at today's rate, and an amount typed in USD can be rounded up to the comps' step. Saving stores a pending bonus (in the currency) for the issuing process and
// clears the amounts, title and comment so the same bonus is not saved twice.
export default function IssueBonusForm({ userId, onClose, bonusesPath }) {
  const [form, setForm] = useState(null);
  const [state, setState] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    setForm(null);
    setLoadError('');
    vipPrivateAPI.getBonusForm(userId, controller.signal)
      .then((res) => { setForm(res.data); setState(initialBonusForm(res.data)); })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setLoadError(err?.response?.data?.error || 'Failed to load the player\'s accounts.');
      });
    return () => controller.abort();
  }, [userId]);

  const set = (key) => (value) => {
    setSaved(null);
    setState((s) => ({ ...s, [key]: value }));
  };
  const accounts = form?.accounts || [];
  // convert: a change to the currency amount, the USD value or the account; the other field follows
  const convert = (fn) => (value) => {
    setSaved(null);
    setState((s) => fn(s, accounts, value));
  };
  // the USD value of the amount that will be saved (what the approval threshold is checked on)
  const usd = state ? bonusUSD(state, accounts) : '';
  const rate = state ? rateLine(accountFor(accounts, state.currency)?.usd_rate, 'USD', state.currency) : null;
  const approval = needsManualApproval(usd, form?.manual_approval_usd);
  const error = state ? bonusFormError(state, accounts) : '';

  const submit = async (e) => {
    e.preventDefault();
    if (error || saving) return;
    setSaving(true);
    setSaveError('');
    try {
      const res = await vipPrivateAPI.issueBonus(bonusPayload(userId, state));
      setSaved(res.data);
      setState((s) => ({ ...s, amount: '', usd: '', rounded: false, title: '', comment: '' }));
    } catch (err) {
      setSaveError(err?.response?.data?.error || 'Failed to issue the bonus.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div id="issue-bonus" className="-mx-5 -mb-5 mt-4 flex flex-col gap-5 rounded-b-lg border-t border-white/10 bg-surface px-5 pb-5 pt-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="flex items-center gap-2.5 text-sm font-bold text-ink">
          <span className="h-2 w-2 rounded-full bg-cta" aria-hidden="true" />Issue bonus
        </h2>
        <p className="text-xs font-medium text-ink-dim">Saved as pending; the issuing process picks it up.</p>
      </div>

      {loadError && <Alert variant="error"><AlertDescription>{loadError}</AlertDescription></Alert>}
      {!form && !loadError && <div className="flex justify-center py-4"><Spinner /></div>}
      {form && accounts.length === 0 && (
        <p className="text-xs font-medium text-ink-dim">The player has no accounts a bonus can be issued in.</p>
      )}

      {form && state && accounts.length > 0 && (
        <form onSubmit={submit} className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-12">
            <Field label="Account" htmlFor="ib-account" className="sm:col-span-2 lg:col-span-3">
              <Select value={state.currency} onValueChange={convert(withCurrency)}>
                <SelectTrigger id="ib-account" className="h-11"><SelectValue placeholder="Currency" /></SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => (
                    <SelectItem key={a.currency} value={a.currency} disabled={!a.usd_rate}>
                      {a.currency} · balance {usdText(a.balance_usd_cents)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <BonusAmountFields
              state={state} accounts={accounts} usd={usd} rate={rate} approval={approval}
              onAmount={convert(withAmount)} onUSD={convert(withUSD)} onMode={convert(withMode)}
              onRound={() => convert(applyRounding)()} onUndoRound={() => convert(undoRounding)()}
            />

            <Field label="Title" htmlFor="ib-title" className="sm:col-span-2 lg:col-span-4">
              <Input id="ib-title" value={state.title} onChange={(e) => set('title')(e.target.value)} maxLength={200} placeholder="e.g. VIP reload bonus" className="h-11" />
            </Field>

            <Field label="Wagering (x)" htmlFor="ib-wager" className="lg:col-span-2">
              <Input id="ib-wager" inputMode="decimal" value={state.wagering} onChange={(e) => set('wagering')(e.target.value)} className="h-11" />
            </Field>
            <Field label="Duration (days)" htmlFor="ib-days" className="lg:col-span-2">
              <Input id="ib-days" inputMode="numeric" value={state.durationDays} onChange={(e) => set('durationDays')(e.target.value)} className="h-11" />
            </Field>
            <Field label="Comment (optional)" htmlFor="ib-comment" className="sm:col-span-2 lg:col-span-4">
              <Textarea id="ib-comment" rows={1} value={state.comment} onChange={(e) => set('comment')(e.target.value)} maxLength={1000} placeholder="Internal note for the VIP team" className="min-h-[44px] resize-y" />
            </Field>
          </div>

          {approval && (
            <p className="flex items-center gap-1.5 text-xs font-medium text-warning">
              <AlertTriangle className="h-3.5 w-3.5" />
              Above {formatAmount(String(form.manual_approval_usd))} USD: it will need manual approval before it is issued.
            </p>
          )}

          <div className="flex flex-wrap items-center justify-end gap-3">
            {saved && (
              <span className="mr-auto inline-flex items-center gap-2 text-sm font-medium text-positive">
                <Check className="h-4 w-4" /> Bonus saved as pending: {saved.title} ({formatAmount(storedAmount(saved.amount), saved.currency)})
                {bonusesPath && <Link to={bonusesPath} className="font-bold underline">View bonuses</Link>}
              </span>
            )}
            {!saved && (saveError || (error && state.amount !== '')) && (
              <span className="mr-auto text-xs font-medium text-negative">{saveError || error}</span>
            )}
            <Button type="button" variant="outline" className="h-11 px-5" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="h-11 px-5" disabled={Boolean(error) || saving}>{saving ? 'Issuing…' : 'Issue bonus'}</Button>
          </div>
        </form>
      )}
    </div>
  );
}
